import React, { useEffect, useMemo, useState } from "react";
import { t } from "../../i18n/locale";

export interface Pager<T> {
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  totalItems: number;
  pageItems: T[];
}

/**
 * Straničenje liste koja je već u memoriji. Stranica se skraćuje kad se lista
 * smanji (filter, brisanje), inače bi korisnik ostao na praznoj stranici bez
 * ikakve poruke.
 */
export function usePagedList<T>(items: T[], pageSize: number): Pager<T> {
  const [page, setPage] = useState(1);
  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageItems = useMemo(() => {
    const start = (Math.min(page, totalPages) - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, page, totalPages, pageSize]);

  return { page, setPage, totalPages, totalItems, pageItems };
}

interface MesPaginationProps<T> {
  pager: Pager<T>;
  /** Genitiv množine za brojač, npr. "primki". */
  itemLabel?: string;
  className?: string;
}

const MesPagination = <T,>({ pager, itemLabel, className }: MesPaginationProps<T>) => {
  const { page, setPage, totalPages, totalItems } = pager;
  if (totalPages <= 1) return null;

  return (
    <div className={`mes-pagination${className ? ` ${className}` : ""}`}>
      <button
        type="button"
        className="btn-secondary"
        disabled={page <= 1}
        onClick={() => setPage(page - 1)}
      >
        {t("← Prethodna")}
      </button>
      <span className="mes-pagination-info">
        {t("Stranica {page} / {total}", { page, total: totalPages })}
        <span className="mes-pagination-count">
          ({totalItems}
          {itemLabel ? ` ${itemLabel}` : ""})
        </span>
      </span>
      <button
        type="button"
        className="btn-secondary"
        disabled={page >= totalPages}
        onClick={() => setPage(page + 1)}
      >
        {t("Sljedeća →")}
      </button>
    </div>
  );
};

export default MesPagination;
